"use client";

import {
  type RefObject,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { isWindowDefined, type Vector2Like } from "@huub-web/utils";

/**
 * Turns a draggable element into a draggable element with a handle.
 *
 * @todo Mouse leaving window should stop dragging.
 */
export function useDragReposition(
  handleRef: RefObject<HTMLElement | null>,
  targetRef: RefObject<HTMLElement | null>,
  isActive: boolean = true,
  options = { padding: 16 },
) {
  const isDraggingRef = useRef(false);
  const [isDragging, setIsDragging] = useState(false);
  const currentRect = useRef<DOMRect | null>(null);
  const initialMousePosition = useRef<Vector2Like>({ x: 0, y: 0 });

  const handleMouseDown = useCallback((e: MouseEvent) => {
    if (!targetRef.current) {
      return;
    }

    isDraggingRef.current = true;
    setIsDragging(true);

    currentRect.current = targetRef.current.getBoundingClientRect();
    initialMousePosition.current = {
      x: e.clientX,
      y: e.clientY,
    };
  }, []);

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (
        !isDraggingRef.current ||
        !targetRef.current ||
        !currentRect.current
      ) {
        return;
      }

      const deltaX = e.clientX - initialMousePosition.current.x;
      const deltaY = e.clientY - initialMousePosition.current.y;

      const newX = currentRect.current.left + deltaX;
      const newY = currentRect.current.top + deltaY;

      // Get viewport dimensions
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      // Get current element dimensions from stored rect
      const rect = currentRect.current;

      // Calculate bounds with padding
      const minX = options.padding;
      const maxX = viewportWidth - rect.width - options.padding;
      const minY = options.padding;
      const maxY = viewportHeight - rect.height - options.padding;

      // Clamp the position within bounds
      const clampedX = Math.min(Math.max(newX, minX), maxX);
      const clampedY = Math.min(Math.max(newY, minY), maxY);

      targetRef.current.style.left = `${clampedX}px`;
      targetRef.current.style.top = `${clampedY}px`;
    },
    [options.padding],
  );

  const handleMouseUp = useCallback(() => {
    isDraggingRef.current = false;
    setIsDragging(false);
  }, []);

  useEffect(() => {
    if (!handleRef.current || !isActive || !isWindowDefined()) {
      return;
    }

    handleRef.current.addEventListener("mousedown", handleMouseDown);
    window.addEventListener("mousemove", handleMouseMove);
    window.addEventListener("mouseup", handleMouseUp);

    return () => {
      if (!handleRef.current) {
        return;
      }
      handleRef.current.removeEventListener("mousedown", handleMouseDown);

      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
    };
  }, [handleMouseDown, handleMouseMove, handleMouseUp, isActive]);

  return { isDragging };
}
